import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import { connect } from "react-redux";
import { createStructuredSelector } from "reselect";
import { withTranslation } from "react-i18next";
import { Input, Form, Button, Space, Typography, Select, Table, Row, Col } from "antd";
import { PhysicianFormItem } from "./form/PhysicianFormItem";
import { selectIsFetching, selectPatientCycles, selectRxInstructionSettings } from "../store/selector";
import { ApiNames, searchProvidersByTypeAPI } from "../api/api";
import ACTIONS from "../store/action";
import { TextWithLabel } from "./TextWithLabel";
import { EditButton } from "./EditButton";
import { PatientRecipientTable } from "./PatientRecipientTable";
import { capitalizeFirstLetter } from "../utils/string";
import { TRUE } from "../constant/settings";
import { optionsSearch } from "../utils/search";
import { SEC_FONT_GREY } from "../constant/colors";
import { NumericalInput } from "./form/NumericalInput";
import { REFILL_MAX } from "../constant/cycles";

const { Text, Paragraph } = Typography;
const { Option } = Select;

const StyledFormItem = styled(Form.Item)`
  .ant-row {
    display: block;
  }
`;

const recipients = (t) => ({
  physician: { text: t("patient.recipient.physician"), value: "physician" },
  patient: { text: t("patient.recipient.patient"), value: "patient" }
});

const fnTeamProvider = (t) => ({
  text: t("patient.recipient.financialNavigationTeam"),
  value: "finantialNavigationTeam" //This typo in finantial is saved like this in the db, need to fix when db is fixed.
});

const FNRecipientTable = (t, fnTeamRecipient) => (
  <Table
    dataSource={[
      fnTeamRecipient
        ? {
            phone: fnTeamRecipient.phone,
            fax: fnTeamRecipient.fax,
            taxId: fnTeamRecipient.taxId,
            address: fnTeamRecipient.address,
            key: fnTeamRecipient
          }
        : {}
    ]}
    columns={[
      {
        title: t("patient.general.phone"),
        dataIndex: "phone",
        render: (text, item) => (item.phone ? item.phone : "-"),
        key: "phone"
      },
      {
        title: t("patient.general.fax"),
        dataIndex: "fax",
        render: (text, item) => (item.fax ? item.fax : "-"),
        key: "fax"
      },
      {
        title: t("patient.general.tax_id"),
        dataIndex: "taxId",
        render: (text, item) => (item.taxId ? item.taxId : "-"),
        key: "taxId"
      },
      {
        title: t("patient.general.address"),
        dataIndex: "address",
        render: (text, item) => (item.address ? item.address : "-"),
        key: "address"
      }
    ]}
    pagination={false}
  />
);

const getRecipientField = (t, recipientType, patient) => {
  const firstName = patient?.patient?.first_name ? patient.patient.first_name : "";
  const middleName = patient?.patient?.middle_name ? patient.patient.middle_name : "";
  const lastName = patient?.patient?.last_name ? patient.patient.last_name : "";
  const patientName = firstName || lastName ? `(${firstName}${middleName ? ` ${middleName}` : ""} ${lastName})` : "";
  if (recipientType === recipients(t).physician.value) {
    return capitalizeFirstLetter(recipientType);
  } else if (recipientType === recipients(t).patient.value) {
    return `${capitalizeFirstLetter(recipientType)} ${patientName}`;
  } else {
    //its a fnTeamProvider
    return `${capitalizeFirstLetter(fnTeamProvider(t).text)} - ${
      patient.fnTeamRecipient ? patient.fnTeamRecipient.name : ""
    } `;
  }
};

const FN_TEAM_TYPE = "finantialNavigationTeam";

const PatientDrugInfoComponent = ({
  t,
  patient,
  order,
  cycles,
  rxInstructionSettings,
  setEdited,
  isFetching,
  bulkChangeAct
}) => {
  const [editable, setEditable] = useState(false);
  const [form] = Form.useForm();
  const [fnTeamProviders, setFnTeamProviders] = useState([]);
  const fnTeamFetched = useRef(false);

  useEffect(() => {
    if (!isFetching) {
      setEditable(false);
    }
  }, [isFetching]);

  // Lazy-fetch FN team providers when first entering edit mode
  useEffect(() => {
    if (editable && !fnTeamFetched.current) {
      fnTeamFetched.current = true;
      searchProvidersByTypeAPI(FN_TEAM_TYPE)
        .then((res) => setFnTeamProviders(res?.data?.providers || []))
        .catch(() => setFnTeamProviders([]));
    }
  }, [editable]);

  useEffect(() => {
    setSelectedPhysician(() => patient?.physician || null);
    const instructions = (patient.rx && patient.rx.instructions) || "";
    const number = (patient.rx && patient.rx.number) || "";
    form.setFieldsValue({
      recipientType:
        patient.recipientType === fnTeamProvider(t).value ? patient.fnTeamRecipient.id : patient.recipientType,
      instructions,
      number,
      refills: order?.refills
    });
  }, [patient, form, t]);

  const onSubmit = (values) => {
    setEdited(false);
    const pharmacyPatient = {
      id: order.pharmacyPatient,
      pharmacyPatient: order.pharmacyPatient,
      recipientType: typeof values.recipientType === "number" ? fnTeamProvider(t).value : values.recipientType || "",
      physician: selectedPhysician?.id ? String(selectedPhysician.id) : null,
      rx: {
        instructions: values.instructions,
        number: values.number
      },
      refills: values.refills,
      fnTeamRecipient: typeof values.recipientType === "number" ? values.recipientType : undefined
    };

    const updateCycles = cycles.map((cycle) => {
      let refills = cycle.refills;
      if (cycle.id === order.id) {
        refills = values.refills;
      } else if (values.refills && cycle.orderIndex > order.orderIndex) {
        const newRefills = values.refills - (cycle.orderIndex - order.orderIndex);
        refills = newRefills >= 0 ? newRefills : null;
      }
      return {
        ...cycle,
        refills
      };
    });

    bulkChangeAct(pharmacyPatient, updateCycles);
  };
  const edit = () => {
    setEdited(true);
    setEditable(!editable);
  };
  const cancel = () => {
    setEditable(false);
    setEdited(false);
    form.resetFields();
  };

  const { id, potentialSaving, physician, recipientType, fnTeamRecipient, orderFrequency, isActive, rx, rxnormDrug } =
    patient;
  const { refills } = order;

  const [selectedPhysician, setSelectedPhysician] = useState(physician || {});
  const { address, city, email, phone_number } = patient.patient;
  const { drugDosage, dosageUnit } = order;
  const dosageText = drugDosage && dosageUnit ? `${drugDosage} ${dosageUnit}` : "";
  const instructions = rx && rx.instructions ? rx.instructions : "";
  const number = rx && rx.number ? rx.number : "";

  const patientAddress = address ? address : address && city ? `${address}, ${city}` : null;
  const patientPhone = phone_number;
  const patientEmail = email;

  return (
    <div style={{ padding: "0px 16px 0px 16px" }}>
      <Row style={{ display: "flex", justifyContent: "space-between" }}>
        <Text strong>{t("patient.general.medication_order_info")}</Text>
        {!editable ? <EditButton id="cc-edit-medorder-btn" disabled={!isActive} onClick={edit} /> : null}
      </Row>
      <Row gutter={[32, 0]}>
        <Col>
          <Row gutter={[32, 0]} justify="space-between">
            <Col span={12}>
              <TextWithLabel
                label={t("patient.general.drug_name")}
                text={rxnormDrug && rxnormDrug.tmDisplayName}
                dataTMId="cct-drug-name"
                expandable={true}
                rowsNo={3}
                isCapitalizeFirstLetter={false}
              />
            </Col>
            <Col span={12}>
              <TextWithLabel
                label={<Text style={{ color: SEC_FONT_GREY }}>{t("patient.general.dosage")}</Text>}
                text={dosageText}
                dataTMId="cct-dosage-quantity"
              />
            </Col>
          </Row>
          <Row gutter={[32, 0]} justify="space-between">
            <Col span={12}>
              <TextWithLabel
                label={t("patient.general.hcpcs")}
                text={rxnormDrug && rxnormDrug.hcpcs}
                dataTMId="cct-drug-manufacturer"
              />
            </Col>
            <Col span={12}>
              <TextWithLabel
                label={t("patient.general.drug_manufacturer")}
                text={potentialSaving && (potentialSaving.providedBy || potentialSaving.assistanceProgram?.providedBy)}
                dataTMId="cct-drug-manufacturer"
              />
            </Col>
          </Row>
          <Form form={form} onFinish={(values) => onSubmit(values)}>
            <Row gutter={[32, 0]} justify="space-between">
              <Col span={12}>
                <NumericalInput
                  name="refills"
                  dataTMId="cct-refills"
                  defaultValue={refills}
                  placeholder={t("form.refills.placeholder")}
                  editable={editable}
                  showLabel={true}
                  label={t("form.refills.label")}
                  labelStyle={{ color: SEC_FONT_GREY }}
                  min={0}
                  max={REFILL_MAX}
                />
              </Col>
              <Col span={12}>
                <TextWithLabel
                  label={t("patient.general.order_frequency")}
                  text={orderFrequency ? `${orderFrequency}` : "-"}
                  dataTMId="cct-order-frequency"
                />
              </Col>
            </Row>
            <Space direction="vertical">
              <PhysicianFormItem
                dataTMId="cct-physician"
                defaultValue={selectedPhysician}
                onPhysicianSelected={(value) => {
                  setSelectedPhysician(value);
                }}
                onClear={() => setSelectedPhysician(null)}
                label={t("form.physician.label")}
                style={{ display: "inline-block", marginBottom: 0 }}
                editable={editable}
                placeholder={t("patient.general.physician_selector_placeholder")}
              />
              <TextWithLabel
                label={t("patient.recipient.recipient")}
                popover={recipientType === recipients(t).patient.value || recipientType === fnTeamProvider(t).value}
                popoverContent={
                  recipientType === recipients(t).patient.value
                    ? PatientRecipientTable({
                        t,
                        dataSource: [{ patientPhone, patientEmail, patientAddress, key: id }]
                      })
                    : recipientType === fnTeamProvider(t).value && FNRecipientTable(t, fnTeamRecipient)
                }
              />
              <Form.Item
                name="recipientType"
                colon={false}
                style={{ display: "inline-block", marginBottom: 0, minWidth: "100px" }}
              >
                {!editable ? (
                  <Text data-tm-id="cct-recipient">
                    {recipientType
                      ? getRecipientField(t, recipientType, patient)
                      : t("patient.recipient.recipient_placeholder")}
                  </Text>
                ) : (
                  <Select
                    data-tm-id="cct-recipient"
                    placeholder={t("patient.recipient.recipient_selector_placeholder")}
                    allowClear={true}
                    showSearch={true}
                    filterOption={optionsSearch}
                    dropdownMatchSelectWidth={false}
                  >
                    {Object.values(recipients(t)).map((r, index) => (
                      <Option key={index} value={r.value}>
                        {r.text}
                      </Option>
                    ))}
                    {fnTeamProviders.map((p) => (
                      <Option key={p.id} value={p.id}>
                        {p.name}
                      </Option>
                    ))}
                  </Select>
                )}
              </Form.Item>
              {rxInstructionSettings?.value === TRUE && (
                <StyledFormItem
                  name="instructions"
                  colon={false}
                  label={<Text style={{ color: SEC_FONT_GREY }}>{t("form.rx_instructions.label")}</Text>}
                  style={{ display: "inline-block", marginBottom: 0 }}
                >
                  {!editable ? (
                    <Paragraph
                      ellipsis={{
                        rows: 1,
                        expandable: instructions && instructions.length > 30,
                        symbol: "more"
                      }}
                      style={{ marginBottom: 0 }}
                      data-tm-id="cct-rx-insutructions"
                    >
                      {instructions && typeof instructions === "string"
                        ? instructions
                        : t("form.rx_instructions.empty_placeholder")}
                    </Paragraph>
                  ) : (
                    <Input
                      data-tm-id="cct-rx-insutructions"
                      disabled={!editable}
                      placeholder={t("form.rx_instructions.placeholder")}
                    />
                  )}
                </StyledFormItem>
              )}
              <StyledFormItem
                name="number"
                colon={false}
                label={<Text style={{ color: SEC_FONT_GREY }}>{t("form.rx_number.label")}</Text>}
                style={{ display: "inline-block", marginBottom: 0 }}
              >
                {!editable ? (
                  <Paragraph
                    data-tm-id="cct-rx-number"
                    style={{ marginBottom: 0 }}
                    ellipsis={{ rows: 1, expandable: number && number.length > 30, symbol: "more" }}
                  >
                    {number && typeof number === "string" ? number : t("form.rx_number.empty_placeholder")}
                  </Paragraph>
                ) : (
                  <Input
                    data-tm-id="cct-rx-number"
                    disabled={!editable}
                    placeholder={t("form.rx_number.placeholder")}
                  />
                )}
              </StyledFormItem>
              {editable ? (
                <Form.Item style={{ paddingTop: 16 }}>
                  <Space>
                    <Button onClick={cancel} id="cc-edit-medorder-cancel">
                      {t("cancel")}
                    </Button>
                    <Button loading={isFetching} id="cc-edit-medorder-submit" type="primary" htmlType="submit">
                      {t("save")}
                    </Button>
                  </Space>
                </Form.Item>
              ) : null}
            </Space>
          </Form>
        </Col>
      </Row>
    </div>
  );
};

const mapStateToProps = createStructuredSelector({
  rxInstructionSettings: selectRxInstructionSettings,
  cycles: selectPatientCycles,
  isFetching: selectIsFetching([ApiNames.BulkChange, ApiNames.PatientPhiInfo])
});

const mapDispatchToProps = (dispatch) => ({
  bulkChangeAct: (pharmacyPatient, orders) => dispatch(ACTIONS.bulkChangeAction(pharmacyPatient, orders))
});

const PatientDrugInfo = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(PatientDrugInfoComponent));

export { PatientDrugInfo };
